// src/pages/home/Gallery.jsx
import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import PageSectionsRenderer from "../../components/common/PageSectionsRenderer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { FaArrowLeft, FaArrowRight, FaMinus, FaPlay, FaPlus, FaTimes, FaUndo } from "react-icons/fa";

const isExternalUrl = (value = "") => /^https?:\/\//i.test(String(value || "").trim());
const isDirectVideoFile = (url = "") =>
  /\.(mp4|webm|mov|avi|m4v|ogv|ogg)(?:[?#].*)?$/i.test(String(url || "").trim());

const extractYouTubeId = (rawUrl = "") => {
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (host === "youtu.be") {
      return url.pathname.split("/").filter(Boolean)[0] || "";
    }
    if (host.includes("youtube.com")) {
      if (url.pathname === "/watch") return url.searchParams.get("v") || "";
      const parts = url.pathname.split("/").filter(Boolean);
      if (["embed", "shorts", "live"].includes(parts[0])) return parts[1] || "";
    }
  } catch (_err) {
    return "";
  }
  return "";
};

const toYouTubeEmbedUrl = (rawUrl = "") => {
  const id = extractYouTubeId(rawUrl);
  return id ? `https://www.youtube.com/embed/${id}` : "";
};

const toVimeoEmbedUrl = (rawUrl = "") => {
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (!host.includes("vimeo.com")) return "";
    const parts = url.pathname.split("/").filter(Boolean);
    if (!parts.length) return "";
    const id = parts[0] === "video" ? parts[1] : parts[0];
    return /^\d+$/.test(id || "") ? `https://player.vimeo.com/video/${id}` : "";
  } catch (_err) {
    return "";
  }
};

const toEmbedUrl = (rawUrl = "") => toYouTubeEmbedUrl(rawUrl) || toVimeoEmbedUrl(rawUrl);
const normalizeCategory = (value) => String(value || "").trim().toLowerCase();
const hostLabel = (rawUrl = "") => {
  try {
    return new URL(rawUrl).hostname.replace(/^www\./i, "");
  } catch (_err) {
    return "External link";
  }
};

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isClosingVideo, setIsClosingVideo] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.25;

  // Load gallery media
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await API.get("/gallery");
        const list = Array.isArray(res) ? res : [];
        setItems(list);
      } catch (err) {
        console.error("Failed to load gallery", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const photos = useMemo(
    () => items.filter((item) => String(item?.file_type || "").toLowerCase() === "image"),
    [items]
  );
  const videos = useMemo(
    () => items.filter((item) => String(item?.file_type || "").toLowerCase() === "video"),
    [items]
  );
  const playerVideos = useMemo(() => {
    const tagged = videos.filter((item) => normalizeCategory(item?.category) === "player_videos");
    return tagged.length ? tagged : videos;
  }, [videos]);

  const selectedIndex = useMemo(
    () => (selectedImage ? photos.findIndex((img) => img.id === selectedImage.id) : -1),
    [photos, selectedImage]
  );

  const closeImage = () => {
    setSelectedImage(null);
    setImageZoom(1);
  };
  const clampZoom = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
  const increaseZoom = () => setImageZoom((prev) => clampZoom(prev + ZOOM_STEP));
  const decreaseZoom = () => setImageZoom((prev) => clampZoom(prev - ZOOM_STEP));
  const resetZoom = () => setImageZoom(1);
  const openVideo = (videoItem) => {
    if (!videoItem) return;
    setIsClosingVideo(false);
    setSelectedVideo(videoItem);
  };
  const closeVideo = () => {
    if (!selectedVideo || isClosingVideo) return;
    setIsClosingVideo(true);
    window.setTimeout(() => {
      setSelectedVideo(null);
      setIsClosingVideo(false);
    }, 220);
  };

  const goPrev = (e) => {
    e?.stopPropagation();
    if (selectedIndex === -1 || photos.length === 0) return;
    const prevIndex = (selectedIndex - 1 + photos.length) % photos.length;
    setSelectedImage(photos[prevIndex]);
    setImageZoom(1);
  };

  const goNext = (e) => {
    e?.stopPropagation();
    if (selectedIndex === -1 || photos.length === 0) return;
    const nextIndex = (selectedIndex + 1) % photos.length;
    setSelectedImage(photos[nextIndex]);
    setImageZoom(1);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!selectedImage && !selectedVideo) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (selectedVideo) closeVideo();
        else closeImage();
        return;
      }
      if (selectedVideo) return;
      if (event.key === "ArrowLeft") goPrev(event);
      else if (event.key === "ArrowRight") goNext(event);
      else if (event.key === "+" || event.key === "=") increaseZoom();
      else if (event.key === "-" || event.key === "_") decreaseZoom();
      else if (event.key === "0") resetZoom();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage, selectedVideo, selectedIndex, photos.length, isClosingVideo]);

  return (
    <>
      <Navbar />

      <main className="bg-slate-50">
        {/* ===== HERO SECTION ===== */}
        <section className="bg-white py-16 text-center border-b border-slate-200">
          <div className="max-w-screen-lg mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">Our Gallery</h1>
            <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
              Photos and player videos capturing moments from our journey of triumph and resilience.
            </p>
          </div>
        </section>

        {/* ===== PHOTO GRID ===== */}
        <section className="py-20 px-4">
          <div className="max-w-screen-xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">Photo Gallery</h2>
            {loading ? (
              <div className="text-center text-slate-500">Loading gallery...</div>
            ) : photos.length === 0 ? (
              <div className="text-center bg-white p-12 rounded-xl shadow-md text-slate-500">
                No photos have been uploaded yet.
              </div>
            ) : (
              <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4">
                {photos.map((img) => (
                  <button
                    key={img.id}
                    onClick={() => {
                      setSelectedImage(img);
                      setImageZoom(1);
                    }}
                    className="relative block w-full overflow-hidden rounded-xl shadow-md group transform transition-transform duration-300 ease-in-out hover:scale-105"
                  >
                    <img
                      src={resolveAssetUrl(img.url || img.file_path)}
                      alt={img.title || "Gallery image"}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                     {img.title && (
                      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                        <p className="text-sm font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {img.title}
                        </p>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ===== PLAYER VIDEOS ===== */}
        <section className="pb-20 px-4">
          <div className="max-w-screen-xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">Player Videos</h2>
            {loading ? (
              <div className="text-center text-slate-500">Loading videos...</div>
            ) : playerVideos.length === 0 ? (
              <div className="text-center bg-white p-12 rounded-xl shadow-md text-slate-500">
                No player videos added yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {playerVideos.map((video) => {
                  const rawPath = String(video?.url || video?.file_path || "").trim();
                  const mediaUrl = resolveAssetUrl(rawPath, { folder: "gallery" });
                  const isExternalVideo = isExternalUrl(rawPath);
                  const embedUrl = isExternalVideo ? toEmbedUrl(mediaUrl) : "";
                  const isLinkCard = isExternalVideo && !embedUrl && !isDirectVideoFile(mediaUrl);
                  const videoPayload = {
                    id: video.id,
                    title: video.title || "Player video",
                    mediaUrl,
                    embedUrl,
                    isLinkCard,
                    isExternalVideo,
                  };

                  return (
                    <article
                      key={video.id}
                      className="overflow-hidden rounded-2xl bg-white shadow-md border border-slate-200"
                    >
                      <button
                        type="button"
                        onClick={() => openVideo(videoPayload)}
                        className="block w-full text-left"
                        aria-label={`Play ${video.title || "player video"}`}
                      >
                        <div className="relative aspect-video bg-slate-100 overflow-hidden">
                          {isLinkCard ? (
                            <div className="h-full w-full flex flex-col items-center justify-center gap-2 bg-slate-900 px-4 text-center text-white">
                              <FaPlay className="text-3xl" />
                              <p className="text-sm font-semibold">External video link</p>
                              <p className="text-xs text-slate-300">{hostLabel(mediaUrl)}</p>
                            </div>
                          ) : (
                            <video
                              src={mediaUrl}
                              preload="metadata"
                              muted
                              className="w-full h-full object-cover"
                            />
                          )}
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-white/90 text-slate-900">
                              <FaPlay />
                            </span>
                          </div>
                        </div>
                      </button>
                      <div className="p-4">
                        <p className="font-semibold text-slate-900 truncate">
                          {video.title || "Player video"}
                        </p>
                        <p className="mt-2 text-sm font-semibold text-blue-600">Play video</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <PageSectionsRenderer pageKey="gallery" />
      </main>
      
      {/* ===== LIGHTBOX MODAL ===== */}
      {selectedImage && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 animate-fade-in"
          onClick={closeImage}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={closeImage}
            aria-label="Close image viewer"
            className="absolute top-4 right-4 z-10 bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
          >
            <FaTimes size={24} />
          </button>

          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={decreaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom out"
            >
              <FaMinus size={14} />
            </button>
            <button
              type="button"
              onClick={increaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom in"
            >
              <FaPlus size={14} />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Reset zoom"
            >
              <FaUndo size={14} />
            </button>
          </div>

          {/* Previous Button */}
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous image"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors"
          >
            <FaArrowLeft size={24} />
          </button>

          {/* Next Button */}
          <button
            type="button"
            onClick={goNext}
            aria-label="Next image"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors"
          >
            <FaArrowRight size={24} />
          </button>

          <div
            className="relative max-w-5xl w-full max-h-[92vh] p-2 sm:p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-xl overflow-hidden bg-black/80">
              <div
                className="flex items-center justify-center p-2 sm:p-3 bg-black overflow-auto"
                onWheel={(event) => {
                  event.preventDefault();
                  if (event.deltaY < 0) {
                    increaseZoom();
                  } else {
                    decreaseZoom();
                  }
                }}
              >
                <img
                  src={resolveAssetUrl(selectedImage.url || selectedImage.file_path)}
                  alt={selectedImage.title || "Selected image"}
                  className="max-h-[78vh] w-auto max-w-full object-contain"
                  style={{
                    transform: `scale(${imageZoom})`,
                    transition: "transform 120ms ease-out",
                  }}
                />
              </div>
              {selectedImage.title && (
                <div className="px-4 py-3 text-center bg-black/85">
                  <p className="text-white text-lg font-semibold">
                    {selectedImage.title}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== VIDEO MODAL ===== */}
      {selectedVideo && (
        <div
          role="dialog"
          aria-modal="true"
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/80 ${isClosingVideo ? "animate-fade-out" : "animate-fade-in"}`}
          onClick={closeVideo}
        >
          <button
            onClick={closeVideo}
            aria-label="Close video player"
            className="absolute top-4 right-4 z-10 bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
          >
            <FaTimes size={24} />
          </button>

          <div
            className="relative max-w-5xl w-full max-h-[85vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-xl overflow-hidden bg-black">
              {selectedVideo.embedUrl ? (
                <iframe
                  src={selectedVideo.embedUrl}
                  title={selectedVideo.title || "Player video"}
                  className="w-full aspect-video"
                  allow="autoplay; encrypted-media; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : selectedVideo.isLinkCard ? (
                <div className="w-full aspect-video flex flex-col items-center justify-center gap-3 text-white px-5 text-center">
                  <FaPlay className="text-3xl" />
                  <p className="font-semibold">This video opens on an external site.</p>
                  <a
                    href={selectedVideo.mediaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 rounded bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors"
                  >
                    Open Video Link
                  </a>
                </div>
              ) : (
                <video
                  src={selectedVideo.mediaUrl}
                  controls
                  autoPlay
                  className="w-full max-h-[75vh] object-contain"
                />
              )}
            </div>
            {selectedVideo.title && (
              <div className="mt-3 text-center">
                <p className="text-white text-lg font-semibold bg-black/30 inline-block px-4 py-2 rounded-lg">
                  {selectedVideo.title}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
       <style>{`
        @keyframes fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes fade-out {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        .animate-fade-out {
          animation: fade-out 0.22s ease-in forwards;
        }
      `}</style>
    </>
  );
};

export default Gallery;
